import webmapApi from "@/api/webmap";
import { loadWebMap, clearWebMap } from "@/helpers/ArcGIS";

const state = {
  webmaps: [],
  activeWebMapId: null,
  webMapLayers: [],
  loading: false,
  hasMore: false,
  searchQuery: "",
  nextStart: 1,
};

const getters = {
  webmaps: (state) => state.webmaps,
  activeWebMapId: (state) => state.activeWebMapId,
  webMapLayers: (state) => state.webMapLayers,
  loading: (state) => state.loading,
  hasMore: (state) => state.hasMore,
  activeWebMap: (state) =>
    state.webmaps.find((wm) => wm.id === state.activeWebMapId) || null,
};

const mutations = {
  SET_WEBMAPS(state, webmaps) {
    state.webmaps = webmaps;
  },
  APPEND_WEBMAPS(state, webmaps) {
    const existing = new Set(state.webmaps.map((w) => w.id));
    state.webmaps = [...state.webmaps, ...webmaps.filter((w) => !existing.has(w.id))];
  },
  SET_ACTIVE_WEBMAP_ID(state, id) {
    state.activeWebMapId = id;
  },
  SET_WEBMAP_LAYERS(state, layers) {
    state.webMapLayers = layers;
  },
  SET_LOADING(state, val) {
    state.loading = val;
  },
  SET_HAS_MORE(state, val) {
    state.hasMore = val;
  },
  SET_SEARCH_QUERY(state, query) {
    state.searchQuery = query;
  },
  SET_NEXT_START(state, val) {
    state.nextStart = val;
  },
  SET_WEBMAP_FIELD_WARNING(state, { id, hasFieldWarning }) {
    const webmap = state.webmaps.find((w) => w.id === id);
    if (webmap) webmap.hasFieldWarning = hasFieldWarning;
  },
};

const actions = {
  async fetchWebMaps({ commit, dispatch }, query = "") {
    commit("SET_LOADING", true);
    commit("SET_SEARCH_QUERY", query);
    try {
      const { items, hasMore, nextStart } = await webmapApi.queryWebMaps({ query, start: 1 });
      commit("SET_WEBMAPS", items);
      dispatch("checkFieldTypeWarnings", items);
      commit("SET_HAS_MORE", hasMore);
      commit("SET_NEXT_START", nextStart);
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  async fetchMoreWebMaps({ commit, state, dispatch }) {
    if (!state.hasMore || state.loading) return;
    commit("SET_LOADING", true);
    try {
      const { items, hasMore, nextStart } = await webmapApi.queryWebMaps({
        query: state.searchQuery,
        start: state.nextStart,
      });
      commit("APPEND_WEBMAPS", items);
      dispatch("checkFieldTypeWarnings", items);
      commit("SET_HAS_MORE", hasMore);
      commit("SET_NEXT_START", nextStart);
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      commit("SET_LOADING", false);
    }
  },

  checkFieldTypeWarnings({ commit }, webmaps) {
    webmaps.forEach(async (webmap) => {
      const hasFieldWarning = await webmapApi.hasUnsupportedFieldTypeLayers(
        webmap.portalItemId,
        webmap.owner
      );
      commit("SET_WEBMAP_FIELD_WARNING", { id: webmap.id, hasFieldWarning });
    });
  },

  async selectWebMap({ commit, state, rootState, dispatch }, webmap) {
    if (state.activeWebMapId === webmap.id) {
      clearWebMap();
      commit('SET_ACTIVE_WEBMAP_ID', null);
      commit('SET_WEBMAP_LAYERS', []);

      if (rootState.shareMap.isSharingMap && rootState.shareMap.host) {
        dispatch('shareMap/updateCurrentWebMapShare', null, { root: true });
      }

      return;
    }

    commit('SET_LOADING', true);

    try {
      await webmapApi.prepareWebMapAccess(webmap.portalItemId, webmap.owner);
      const layers = await loadWebMap(webmap.portalItemId);

      commit('SET_ACTIVE_WEBMAP_ID', webmap.id);
      commit('SET_WEBMAP_LAYERS', layers);

      if (rootState.shareMap.isSharingMap && rootState.shareMap.host) {
        dispatch(
          'shareMap/updateCurrentWebMapShare',
          {
            id: webmap.id,
            portalItemId: webmap.portalItemId,
            owner: webmap.owner,
          },
          { root: true }
        );
      }
    } catch (error) {
      console.error(error);
      throw error;
    } finally {

      commit('SET_LOADING', false);
    }
  },

  async loadSharedWebMap({ commit }, webmap) {
    if (!webmap) {
      clearWebMap();
      commit('SET_ACTIVE_WEBMAP_ID', null);
      commit('SET_WEBMAP_LAYERS', []);
      return;
    }

    commit('SET_LOADING', true);

    try {
      await webmapApi.prepareWebMapAccess(webmap.portalItemId, webmap.owner);
      const layers = await loadWebMap(webmap.portalItemId);

      commit('SET_ACTIVE_WEBMAP_ID', webmap.id);
      commit('SET_WEBMAP_LAYERS', layers);
    } catch (error) {
      console.error('Error cargando WebMap compartido:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
